import * as React from "react";
import styles from "./styles.module.scss";
import ModalBackground from "assets/img-winaz/signup-form/bgpopup.webp";
import LogoWinaz from "assets/img-winaz/logo.webp";
import IconUser from "assets/img-winaz/signup-form/iconusser.webp";
import IconLock from "assets/img-winaz/signup-form/iconpassword.webp";
import BtnCreateAccount from "assets/img-winaz/button/btndawng ki.webp";
import Label29k from "assets/img-winaz/button/label29k.webp";
import IconClose from "assets/img-winaz/button/btnclose.webp";
import { useSignupForm } from "hooks/useSignupForm";
import TurnstileWidget, { TurnstileWidgetHandle } from "components/TurnstileWidget";

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

type CreateAccountModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const CreateAccountModal: React.FunctionComponent<CreateAccountModalProps> = ({
  isOpen,
  onClose,
}) => {
  const turnstileRef = React.useRef<TurnstileWidgetHandle | null>(null);
  const handleResetTurnstile = React.useCallback(() => {
    turnstileRef.current?.reset();
  }, []);
  const {
    username,
    password,
    isSubmitting,
    apiError,
    usernameError,
    passwordError,
    handleSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleTurnstileTokenChange,
    turnstileRequired,
    reset,
  } = useSignupForm({
    requireConfirmPassword: false,
    formId: "register_popup",
    onSuccess: onClose,
    onTurnstileReset: handleResetTurnstile,
  });

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  React.useEffect(() => {
    if (isOpen) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "lp_form_start",
        form_id: "register_popup",
      });
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-account-title"
        style={{ backgroundImage: `url(${ModalBackground})` }}
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Dong"
          disabled={isSubmitting}
        >
          <img src={IconClose} alt="Dong" />
        </button>

        <div className={styles.header}>
          <img src={LogoWinaz} alt="Sunwin" />
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.fieldGroup}>
            <label htmlFor="create-account-username" className={styles.label}>
              <img src={IconUser} alt="User" />
            </label>
            <input
              id="create-account-username"
              type="text"
              placeholder="Tên đăng nhập"
              value={username}
              onChange={handleUsernameChange}
              disabled={isSubmitting}
            />
          </div>
          {usernameError ? (
            <div className={styles.error}>Vui lòng nhập tên đăng nhập</div>
          ) : null}

          <div className={styles.fieldGroup}>
            <label htmlFor="create-account-password" className={styles.label}>
              <img src={IconLock} alt="Password" />
            </label>
            <input
              id="create-account-password"
              type="password"
              placeholder="Mật khẩu"
              value={password}
              onChange={handlePasswordChange}
              disabled={isSubmitting}
            />
          </div>
          {passwordError ? (
            <div className={styles.error}>Vui lòng nhập mật khẩu</div>
          ) : null}

          {turnstileRequired ? (
            <div className={styles.turnstileBlock}>
              <div className={styles.turnstileInner}>
                <TurnstileWidget
                  ref={turnstileRef}
                  className={styles.turnstileWidget}
                  onTokenChange={handleTurnstileTokenChange}
                />
              </div>
            </div>
          ) : null}

          {apiError ? <div className={styles.error}>{apiError}</div> : null}

          <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
            <img src={Label29k} alt="29k" className={styles.submitLabel} />
            <img src={BtnCreateAccount} alt="Tạo tài khoản" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateAccountModal;
